import {get} from 'lodash';
import Loader from 'react-loader-spinner';
import {withTheme} from 'react-jss';
import moment from 'moment';
import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {getNADAMakes, getNADAModels} from '-/api/api';
import {Control, ControlGroup} from '-/components/fields/Control';
import Select from '-/components/fields/Select';
import {getVinVehicleInfo} from '-/util/util';

const YearMakeModel = props => {
    const [lookupFailed, setLookupFailed] = useState(false);
    const [makeOpts, setMakeOpts] = useState([]);
    const [modelOpts, setModelOpts] = useState([]);
    const [loading, setLoading] = useState(false);
    const {
        handleClearFields,
        contactPhone,
        handleChange,
        prioritizedMakes = [],
        showErrors,
        theme = {},
        vehicleInfo: {
            make,
            model,
            year
        } = {},
        yearRange: {
            maxYear: creditorMaxYear,
            minYear: creditorMinYear
        }
    } = props;
    const primaryColor = get(theme, 'colors.primary');
    const calculatedMaxYear = parseInt(moment().add(1, 'year').format('YYYY'), 10);
    const calculatedMinYear = parseInt(moment().subtract(10, 'years').format('YYYY'), 10);
    const maxYear = creditorMaxYear || calculatedMaxYear;
    const minYear = creditorMinYear || calculatedMinYear;
    const yearOptions = new Array(maxYear - minYear + 1).fill(0).map((value, idx) => {
        return maxYear - idx;
    });
    const invalidCSSStyle = {border: '1px solid red'};
    const invalidYearStyle = !year && showErrors ? invalidCSSStyle : {};
    const invalidMakeStyle = !make && showErrors ? invalidCSSStyle : {};
    const invalidModelStyle = !model && showErrors ? invalidCSSStyle : {};

    const handleMakeChange = async() => {
        if (!make) {
            return;
        }

        setLoading(true);

        let newModelOpts = [];
        let newLookupFailed = false;

        try {
            newModelOpts = await getNADAModels({make, year});
        } catch(ex) {
            newLookupFailed = true;
        }

        setLookupFailed(newLookupFailed);
        setModelOpts(newModelOpts);
        setLoading(false);
    };

    const handleYearChange = async() => {
        if (!year) {
            return;
        } 
        
        setLoading(true);
        let newMakeOpts = [];
        let newLookupFailed = false;

        try {
            const allMakes = await getNADAMakes({year});
            const filteredMakes = allMakes.filter(make => !prioritizedMakes.includes(make));

            newMakeOpts = [...prioritizedMakes, ...filteredMakes];
        } catch(ex) {
            newLookupFailed = true;
        }

        setModelOpts([]);
        setMakeOpts(newMakeOpts);
        setLookupFailed(newLookupFailed);
        setLoading(false);
    };

    const onYearChange = selectedYear => {
        handleChange({year: selectedYear});
        handleClearFields(['make', 'model', 'ucgVehicleId']);
    };

    const onMakeChange = selectedMake => {
        handleChange({make: selectedMake});
        handleClearFields(['model', 'ucgVehicleId']);
    };

    const onModelChange = selectedModel => {
        const vinVehicleInfo = getVinVehicleInfo({year, make, model: selectedModel});

        handleChange({
            model: selectedModel,
            vinVehicleInfo
        });
    };

    useEffect(() => {
        handleYearChange(year);
    }, [year]);

    useEffect(() => {
        handleMakeChange(make);
    }, [make]);

    return (
        <Fragment>
            {lookupFailed && (
                <p className="info">
                    There was an issue with the lookup. Please try again.
                    If the problem persists, please contact us at {contactPhone}
                </p>
            )}
            <ControlGroup labelWidth={200}>
                <Control>
                    <Select
                        readOnly
                        style={invalidYearStyle}
                        value={year || ''}
                        name="year"
                        label="Year"
                        onChange={onYearChange}>
                        <option value="">Select a Year</option>
                        {yearOptions.map(yearOpt => {
                            return (
                                <option
                                    key={yearOpt}
                                    value={yearOpt}
                                    selected={year === yearOpt}>
                                    {yearOpt}
                                </option>
                            );
                        })}
                    </Select>
                </Control>
                <Control>
                    <Select
                        readOnly
                        style={invalidMakeStyle}
                        value={make || ''}
                        name="make"
                        label="Make"
                        onChange={onMakeChange}>
                        <option value="">Select a Make</option>
                        {makeOpts.map((makeOpt, i) => {
                            return (
                                <option
                                    key={`${makeOpt}-${i}`}
                                    selected={make === makeOpt}
                                    value={makeOpt}>
                                    {makeOpt}
                                </option>
                            );
                        })}
                    </Select>
                </Control>
                <Control>
                    <Select
                        readonly
                        style={invalidModelStyle}
                        value={model || ''}
                        name="model"
                        label="Model"
                        onChange={onModelChange}>
                        <option value="">Select a Model</option>
                        {modelOpts.map((modelOpt, i) => {
                            return (
                                <option
                                    key={`${modelOpt}-${i}`}
                                    value={modelOpt}
                                    selected={model === modelOpt}>
                                    {modelOpt}
                                </option>
                            );
                        })}
                    </Select>
                </Control>
                {loading && (
                    <Loader
                        className="loader"
                        type="ThreeDots"
                        color={primaryColor}
                        height="100"
                        width="100" />
                )}
            </ControlGroup>
        </Fragment>
    );
};


YearMakeModel.propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleClearFields: PropTypes.func.isRequired,
    showErrors: PropTypes.bool
};

export default withTheme(YearMakeModel);